/**
 * Card component for displaying customer site statistics.
 * Shows counts for active and inactive sites.
 */
import { CardHeader } from '@/components/card-header';
import { LinkRounded } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import { useLocations } from '@/location/hooks/use-locations';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';

interface Props {
  customer: Customer;
}

export function CustomerSitesStatsCard(props: Props) {
  const { locations } = useLocations({
    filter: { customerId: props.customer.id },
  });

  const activeLocations = locations.filter(
    (location) => location.assetCount > 0
  );
  const inactiveLocations = locations.filter(
    (location) => location.assetCount === 0
  );

  return (
    <Paper variant="outlined">
      <CardHeader title="Sites" />
      <Divider />
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} margin={2}>
        <Stack
          padding={2}
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h6">Active</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3">
              {formatNumber(activeLocations.length)}
            </Typography>
            <Tooltip arrow title="View active sites">
              {/* TODO(#15944): Update link to filter the specific locations */}
              <Link
                to="/locations"
                search={{ customers: [props.customer.id], archived: false }}
              >
                <IconButton size="small">
                  <LinkRounded />
                </IconButton>
              </Link>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack
          padding={2}
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h6">Inactive</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3">
              {formatNumber(inactiveLocations.length)}
            </Typography>
            <Tooltip arrow title="View inactive sites">
              {/* TODO(#15944): Update link to filter the specific locations */}
              <Link
                to="/locations"
                search={{ customers: [props.customer.id], archived: true }}
              >
                <IconButton size="small">
                  <LinkRounded />
                </IconButton>
              </Link>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
